import {
  completeCensusActivation,
  startCensusActivation,
  verifyCensusActivationIdentity,
} from "@/api/census-activation-client";
import type { CensusVerificationMethod } from "@/api/census-activation-client";
import {
  type StepBuildCeremony,
  mergeStepBuildProps,
  pageBodyBlockBuildProps,
  sectionPageIntroMotions,
} from "@/components/benefits-discovery-v2/step-sequential-build";
import { fetchCensusIdentity } from "@/hooks/use-census-session";
import { track } from "@cakewalk/frontend-api";
import { Eye, EyeOff, KeyRound, Lock, Mail, Phone, User } from "@cakewalk/ui/icons";
import {
  V2Button,
  V2Card,
  V2CardContent,
  V2FormActions,
  V2FormField,
  V2InlineIconButton,
  V2Input,
  V2InputOTP,
  V2Typography,
} from "@cakewalk/ui/v2";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import { type FormEvent, type ReactNode, useState } from "react";
import { OtpResend } from "./OtpResend";

/**
 * "Activate your account" step for company-census.
 *
 * Existing owners who already have policies with us but no platform login yet
 * activate here, then continue into the census intro. It mirrors the production
 * activation flow (registration-app) but is EMAIL-RESOLVED (no emailed link) and
 * ends at the census intro rather than cockpit/wallet:
 *
 *   1. email     — enter the email on file. The platform API resolves the owner
 *                  via db-service and tells us which identity check to ask for.
 *   2. verify    — SSN last-4 OR date of birth + last name (checked server-side;
 *                  rate-limited with a lockout).
 *   3. password  — choose a password (+ a phone when none is on file) — this
 *                  provisions the Cognito login.
 *   4. otp       — SMS 2FA code on first sign-in (same as login), then we hand
 *                  the freshly-minted identity to the orchestrator.
 *
 * Plain `useState` + `@cakewalk/ui/v2`, matching the sibling login / forgot /
 * create-account steps.
 */

type Phase = "email" | "verify" | "password" | "otp" | "locked";

// Cognito password policy: 8+ chars with upper, lower, and a number. A friendly
// client-side guard so the user isn't bounced by the API.
function isStrongPassword(value: string): boolean {
  return value.length >= 8 && /[a-z]/.test(value) && /[A-Z]/.test(value) && /\d/.test(value);
}

function pad2(v: string): string {
  const digits = v.replace(/\D/g, "").slice(0, 2);
  return digits.padStart(2, "0");
}

export function CompanyCensusActivateStep({
  defaultEmail,
  notice,
  playBuild = false,
  ceremony = null,
  onActivated,
  onBackToLogin,
}: {
  defaultEmail?: string;
  /** Optional explainer shown atop the first step (e.g. when the login step
   *  redirected a known owner here because they have no login yet). */
  notice?: string;
  playBuild?: boolean;
  ceremony?: StepBuildCeremony | null;
  onActivated: (email: string, canManage: boolean) => void;
  onBackToLogin: (email?: string) => void;
}) {
  const [phase, setPhase] = useState<Phase>("email");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Resolved by /start.
  const [token, setToken] = useState("");
  const [method, setMethod] = useState<CensusVerificationMethod>("dob_lastName");
  const [phoneOnFile, setPhoneOnFile] = useState(true);

  // Verify inputs.
  const [ssnLast4, setSsnLast4] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [lastName, setLastName] = useState("");

  // Password inputs.
  const [verifiedToken, setVerifiedToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState("");

  // OTP.
  const [otp, setOtp] = useState("");
  const [maskedDestination, setMaskedDestination] = useState<string | undefined>(undefined);
  // SMS codes can be re-sent; authenticator-app codes can't.
  const [otpViaSms, setOtpViaSms] = useState(false);

  const { eyebrowMotion, titleMotion, descriptionMotion } = sectionPageIntroMotions(
    playBuild,
    ceremony,
  );

  const finish = async () => {
    const identity = await fetchCensusIdentity();
    // tracking-plan: Flow 11 (company_census) > census_activation_completed (P1)
    track("census_activation_completed", { flow_name: "company_census", step_name: "activate" });
    onActivated(email.trim(), identity.canManage);
  };

  const handleStart = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await startCensusActivation(email.trim());
      setToken(result.token);
      setMethod(result.verificationMethod);
      setPhoneOnFile(result.phoneOnFile);
      setPhase("verify");
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't start activation. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setError(undefined);
    try {
      const dob =
        method === "dob_lastName"
          ? `${dobYear.replace(/\D/g, "").slice(0, 4)}-${pad2(dobMonth)}-${pad2(dobDay)}`
          : undefined;
      const result = await verifyCensusActivationIdentity({
        token,
        method,
        ssnLast4: method === "ssn_last4" ? ssnLast4.replace(/\D/g, "") : undefined,
        dob,
        lastName: method === "dob_lastName" ? lastName.trim() : undefined,
      });

      if (result.locked) {
        setPhase("locked");
        return;
      }
      if (!result.match || !result.verifiedToken) {
        const remaining = result.attemptsRemaining ?? 0;
        setError(
          remaining > 0
            ? `That didn't match our records. ${remaining} ${remaining === 1 ? "try" : "tries"} left.`
            : "That didn't match our records.",
        );
        return;
      }
      setVerifiedToken(result.verifiedToken);
      setPhase("password");
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't verify your identity. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const startSignIn = async () => {
    const response = await signIn({ username: email.trim(), password });
    if (response.isSignedIn) {
      await finish();
      return;
    }
    const step = response.nextStep?.signInStep;
    if (step === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
      setMaskedDestination(response.nextStep.codeDeliveryDetails?.destination);
      setOtpViaSms(true);
      setPhase("otp");
      return;
    }
    if (step === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
      setMaskedDestination("your authenticator app");
      setOtpViaSms(false);
      setPhase("otp");
      return;
    }
    setError(
      `Your account is ready. Finish setting up two-factor on the main Cakewalk app, then come back and log in (${step ?? "UNKNOWN_STEP"}).`,
    );
  };

  // Re-send the SMS by restarting the sign-in. <OtpResend> owns the cooldown and
  // only calls this once its countdown hits zero: resolve to restart it, throw
  // to leave the button armed for a retry.
  const resendCode = async () => {
    setError(undefined);
    try {
      const response = await signIn({ username: email.trim(), password });
      if (response.isSignedIn) {
        await finish();
        return;
      }
      if (response.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
        setMaskedDestination(response.nextStep.codeDeliveryDetails?.destination);
        setOtp("");
        return;
      }
      setError("We couldn't resend the code. Try again.");
      throw new Error(`resend-unexpected-step:${response.nextStep?.signInStep ?? "unknown"}`);
    } catch (err) {
      if (err instanceof Error && err.name === "UserAlreadyAuthenticatedException") {
        await finish();
        return;
      }
      setError((prev) => prev ?? (err instanceof Error ? err.message : "Failed to resend code"));
      throw err;
    }
  };

  const handleComplete = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    if (password !== confirmPassword) {
      setError("Those passwords don't match.");
      return;
    }
    if (!isStrongPassword(password)) {
      setError(
        "Use at least 8 characters with an uppercase letter, a lowercase letter, and a number.",
      );
      return;
    }
    if (!phoneOnFile && phone.replace(/\D/g, "").length < 10) {
      setError("Enter a mobile number so we can text your verification code.");
      return;
    }
    setIsLoading(true);
    setError(undefined);
    try {
      await completeCensusActivation({
        verifiedToken,
        password,
        phone: phoneOnFile ? undefined : phone.trim(),
      });
      await startSignIn();
    } catch (err) {
      if (err instanceof Error && err.name === "UserAlreadyAuthenticatedException") {
        await finish();
        return;
      }
      setError(err instanceof Error ? err.message : "We couldn't finish setting up your account.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtp = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await confirmSignIn({ challengeResponse: otp.replace(/\D/g, "") });
      if (result.isSignedIn) {
        await finish();
        return;
      }
      setError(`Authentication incomplete (${result.nextStep?.signInStep ?? "UNKNOWN_STEP"}).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to verify code");
    } finally {
      setIsLoading(false);
    }
  };

  // Key the card by phase so each screen mounts a FRESH subtree. Without this,
  // React reuses the same <form> DOM node across phases (password → otp), and
  // the browser treats that form's autofill as already handled — suppressing
  // the SMS one-time-code suggestion on the OTP field. A fresh form per phase
  // mirrors the login step (whose OTP screen is a separate top-level return).
  const shell = (children: ReactNode) => (
    <div key={phase} className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
      <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
        <V2CardContent className="flex flex-col gap-6 p-6">{children}</V2CardContent>
      </V2Card>
      <div className="flex items-center justify-center gap-2 text-cw-v2-text-muted">
        <Lock className="h-3.5 w-3.5" aria-hidden />
        <V2Typography variant="meta" muted>
          Encrypted &amp; secure. This is the same login you use for your existing benefits.
        </V2Typography>
      </div>
    </div>
  );

  if (phase === "locked") {
    return shell(
      <>
        <div className="flex flex-col gap-2">
          <V2Typography variant="overline" className="text-cw-v2-reliant-red">
            Verification paused
          </V2Typography>
          <V2Typography variant="heading-medium" as="h1" className="font-semibold">
            Too many attempts
          </V2Typography>
          <V2Typography variant="body" muted>
            For your security we&rsquo;ve paused identity verification. Please contact{" "}
            <a className="text-cw-v2-link underline" href="mailto:support@cakewalkbenefits.com">
              support@cakewalkbenefits.com
            </a>{" "}
            and we&rsquo;ll help you finish setting up your account.
          </V2Typography>
        </div>
        <V2FormActions variant="stretch">
          <V2Button
            variant="secondary"
            size="lg"
            fullWidth
            onClick={() => onBackToLogin(email.trim() || undefined)}
            data-track="button-company-census-activate-locked-back"
          >
            Back to login
          </V2Button>
        </V2FormActions>
      </>,
    );
  }

  if (phase === "otp") {
    return shell(
      <>
        <div className="flex flex-col gap-2">
          <V2Typography variant="overline" className="text-cw-v2-reliant-red">
            Verify it&rsquo;s you
          </V2Typography>
          <V2Typography variant="heading-medium" as="h1" className="font-semibold">
            Enter your verification code
          </V2Typography>
          <V2Typography variant="body" muted>
            We sent a code to {maskedDestination ?? "your device"}. Enter it below to finish
            activating your account.
          </V2Typography>
        </div>
        <form onSubmit={handleOtp} className="flex flex-col gap-5">
          <V2InputOTP
            label="Verification code"
            error={error}
            length={6}
            value={otp}
            onValueChange={setOtp}
            autoFocus
          />
          <V2FormActions variant="stretch">
            <V2Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isLoading || otp.replace(/\D/g, "").length === 0}
              data-track="button-company-census-activate-verify-otp"
            >
              Verify and continue
            </V2Button>
          </V2FormActions>
        </form>

        {otpViaSms ? (
          <div className="flex justify-center">
            <OtpResend onResend={resendCode} dataTrack="button-company-census-activate-resend" />
          </div>
        ) : null}
      </>,
    );
  }

  if (phase === "password") {
    return shell(
      <>
        <div className="flex flex-col gap-2">
          <V2Typography variant="overline" className="text-cw-v2-reliant-red">
            Almost there
          </V2Typography>
          <V2Typography variant="heading-medium" as="h1" className="font-semibold">
            Create your password
          </V2Typography>
          <V2Typography variant="body" muted>
            Set a password for your Cakewalk login.
            {phoneOnFile
              ? " We&rsquo;ll text a verification code to the number on file."
              : " We&rsquo;ll text a verification code to the mobile number you enter."}
          </V2Typography>
        </div>
        <form onSubmit={handleComplete} className="flex flex-col gap-5">
          <V2FormField label="Password">
            <V2Input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="••••••••"
              prefix={<Lock className="h-4 w-4" aria-hidden />}
              suffix={
                <V2InlineIconButton
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword((v) => !v)}
                  icon={showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                />
              }
              data-sensitive
            />
          </V2FormField>

          <V2FormField label="Confirm password" error={phoneOnFile ? error : undefined}>
            <V2Input
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="••••••••"
              prefix={<Lock className="h-4 w-4" aria-hidden />}
              data-sensitive
            />
          </V2FormField>

          {!phoneOnFile && (
            <V2FormField label="Mobile number" error={error}>
              <V2Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(555) 555-1234"
                prefix={<Phone className="h-4 w-4" aria-hidden />}
                data-sensitive
              />
            </V2FormField>
          )}

          <V2FormActions variant="stretch">
            <V2Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isLoading || password.length === 0 || confirmPassword.length === 0}
              data-track="button-company-census-activate-complete"
            >
              {isLoading ? "Setting up your account…" : "Create account"}
            </V2Button>
          </V2FormActions>
        </form>
      </>,
    );
  }

  if (phase === "verify") {
    return shell(
      <>
        <div className="flex flex-col gap-2">
          <V2Typography variant="overline" className="text-cw-v2-reliant-red">
            Verify it&rsquo;s you
          </V2Typography>
          <V2Typography variant="heading-medium" as="h1" className="font-semibold">
            Confirm your identity
          </V2Typography>
          <V2Typography variant="body" muted>
            {method === "ssn_last4"
              ? "Enter the last 4 digits of your Social Security number to confirm it's you."
              : "Enter your date of birth and last name to confirm it's you."}
          </V2Typography>
        </div>

        <form onSubmit={handleVerify} className="flex flex-col gap-5">
          {method === "ssn_last4" ? (
            <V2FormField label="Last 4 of SSN" error={error}>
              <V2Input
                value={ssnLast4}
                onChange={(e) => setSsnLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                inputMode="numeric"
                autoComplete="off"
                placeholder="1234"
                prefix={<KeyRound className="h-4 w-4" aria-hidden />}
                data-sensitive
              />
            </V2FormField>
          ) : (
            <>
              <div className="flex gap-3">
                <V2FormField label="Month">
                  <V2Input
                    value={dobMonth}
                    onChange={(e) => setDobMonth(e.target.value.replace(/\D/g, "").slice(0, 2))}
                    inputMode="numeric"
                    placeholder="MM"
                    data-sensitive
                  />
                </V2FormField>
                <V2FormField label="Day">
                  <V2Input
                    value={dobDay}
                    onChange={(e) => setDobDay(e.target.value.replace(/\D/g, "").slice(0, 2))}
                    inputMode="numeric"
                    placeholder="DD"
                    data-sensitive
                  />
                </V2FormField>
                <V2FormField label="Year">
                  <V2Input
                    value={dobYear}
                    onChange={(e) => setDobYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    inputMode="numeric"
                    placeholder="YYYY"
                    data-sensitive
                  />
                </V2FormField>
              </div>
              <V2FormField label="Last name" error={error}>
                <V2Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  placeholder="Owner"
                  prefix={<User className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>
            </>
          )}

          <V2FormActions variant="stretch">
            <V2Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isLoading}
              data-track="button-company-census-activate-verify"
            >
              {isLoading ? "Verifying…" : "Verify and continue"}
            </V2Button>
          </V2FormActions>
        </form>

        <div className="flex justify-center border-t border-cw-v2-border/60 pt-5">
          <V2Button
            variant="link"
            onClick={() => onBackToLogin(email.trim() || undefined)}
            data-track="button-company-census-activate-verify-back"
          >
            Back to login
          </V2Button>
        </div>
      </>,
    );
  }

  // phase === "email"
  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
      <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
        <V2CardContent className="flex flex-col gap-6 p-6">
          <div className="flex flex-col gap-2">
            <V2Typography
              variant="overline"
              {...mergeStepBuildProps(eyebrowMotion, { className: "text-cw-v2-reliant-red" })}
            >
              First time?
            </V2Typography>
            <V2Typography
              variant="heading-medium"
              as="h1"
              {...mergeStepBuildProps(titleMotion, { className: "font-semibold" })}
            >
              Activate your account
            </V2Typography>
            <V2Typography variant="body" muted {...mergeStepBuildProps(descriptionMotion)}>
              Already a Cakewalk customer but haven&rsquo;t logged in before? Enter your email and
              we&rsquo;ll help you set up your account.
            </V2Typography>
          </div>

          {notice && (
            <output className="block rounded-cw-v2-md border border-cw-v2-blue-200 bg-cw-v2-blue-50 p-4">
              <V2Typography variant="body" className="text-cw-v2-blue-600">
                {notice}
              </V2Typography>
            </output>
          )}

          <form
            onSubmit={handleStart}
            className="flex flex-col gap-5"
            data-track="form-company-census-activate-start"
          >
            <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 0, ceremony))}>
              <V2FormField label="Email" error={error}>
                <V2Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  prefix={<Mail className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>
            </div>

            <V2FormActions
              variant="stretch"
              {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 1, ceremony))}
            >
              <V2Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={isLoading || email.trim().length === 0}
                data-track="button-company-census-activate-start"
              >
                {isLoading ? "Checking…" : "Continue"}
              </V2Button>
            </V2FormActions>
          </form>

          <div className="flex flex-col items-center gap-1 border-t border-cw-v2-border/60 pt-5">
            <V2Typography variant="meta" muted>
              Already have an account?
            </V2Typography>
            <V2Button
              variant="link"
              onClick={() => onBackToLogin(email.trim() || undefined)}
              data-track="button-company-census-activate-back-to-login"
            >
              Back to login
            </V2Button>
          </div>
        </V2CardContent>
      </V2Card>

      <div className="flex items-center justify-center gap-2 text-cw-v2-text-muted">
        <Lock className="h-3.5 w-3.5" aria-hidden />
        <V2Typography variant="meta" muted>
          Encrypted &amp; secure. This is the same login you use for your existing benefits.
        </V2Typography>
      </div>
    </div>
  );
}
